import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isSupportedLocale, pickDefaultLocale } from "@/i18n/locale-utils";
import { DEFAULT_LOCALE } from "@/lib/seo/hreflang";

/**
 * Validates the :locale URL parameter and syncs i18next + <html lang>.
 * - Supported locale → render children
 * - Looks like a locale (2 lowercase letters) but unsupported → 404 (so search
 *   engines don't follow phantom URLs and dilute hreflang signals)
 * - Anything else → redirect to user's preferred locale, preserving the rest
 */
export function LocaleGate({ children }: { children: ReactNode }) {
  const { locale } = useParams<{ locale: string }>();
  const location = useLocation();
  const { i18n } = useTranslation();

  const valid = isSupportedLocale(locale);
  const looksLikeLocale = !!locale && /^[a-z]{2}$/.test(locale);

  // Re-render when i18next finishes switching, so children never paint with
  // the previous language.
  const [, setTick] = useState(0);
  useEffect(() => {
    const cb = () => setTick((n) => n + 1);
    i18n.on("languageChanged", cb);
    i18n.on("initialized", cb);
    return () => {
      i18n.off("languageChanged", cb);
      i18n.off("initialized", cb);
    };
  }, [i18n]);

  // Sync i18next DURING render, before children (and their <Seo>) render.
  // Resources are bundled synchronously, so changeLanguage applies immediately;
  // doing this in an effect made the first head write use the previously
  // detected language (the /fr/* → English title race).
  const active = (i18n.resolvedLanguage || i18n.language || "").slice(0, 2);
  if (valid && active !== locale) {
    void i18n.changeLanguage(locale);
  }

  useEffect(() => {
    if (!valid) return;
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale!;
    }
  }, [locale, valid, i18n]);

  if (!valid) {
    // Unsupported locale prefix (e.g. /de/about) → send to the x-default
    // locale, preserving the rest of the path. Client-side this is a replace
    // navigation; the hosting edge turns it into a 301 for crawlers.
    if (looksLikeLocale) {
      const rest = location.pathname.replace(/^\/[a-z]{2}(?=\/|$)/, "");
      const target = `/${DEFAULT_LOCALE}${rest}${location.search}${location.hash}`;
      return <Navigate to={target} replace />;
    }
    // Otherwise, treat the whole path as a route under fallback locale.
    const fallback = pickDefaultLocale();
    const target = `/${fallback}${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={target} replace />;
  }

  // Bundled resources make changeLanguage effectively synchronous; if it ever
  // isn't, hold the paint rather than flash English chrome.
  if ((i18n.resolvedLanguage || i18n.language || "").slice(0, 2) !== locale) {
    return null;
  }

  return <>{children}</>;
}

